import { useEffect, useMemo, useState } from "react";
import { HallOfFameDetail } from "@/components/hall-of-fame/HallOfFameDetail";
import { HallOfFameTile } from "@/components/hall-of-fame/HallOfFameTile";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import {
  decadesWithEntries,
  entriesInDecade,
  HOF_DECADES,
  type HofDecadeId,
} from "@/lib/hall-of-fame.helpers";
import { harmonizeDecadeLabel } from "@/lib/decade-label";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime";

type HallOfFameEntry = {
  id: string;
  title: string;
  subtitle: string | null;
  body: string;
  entry_year: number;
  media_type: string;
  media_url: string;
};

type HallOfFameTimelineProps = {
  entries: HallOfFameEntry[];
};

function decadeLabel(decadeId: HofDecadeId): string {
  if (decadeId === "today") return m.hof_decade_today();
  const decade = HOF_DECADES.find((d) => d.id === decadeId);
  if (!decade) return decadeId;
  const label = m.hof_decade_label({ decade: String(decade.start) });
  return harmonizeDecadeLabel(getLocale(), label, decade.start);
}

export function HallOfFameTimeline({ entries }: HallOfFameTimelineProps) {
  const availableDecades = useMemo(() => decadesWithEntries(entries), [entries]);
  const [decadeId, setDecadeId] = useState<HofDecadeId | null>(availableDecades[0] ?? null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!availableDecades.length) {
      setDecadeId(null);
      setSelectedId(null);
      return;
    }
    if (!decadeId || !availableDecades.includes(decadeId)) {
      setDecadeId(availableDecades[0]);
    }
  }, [availableDecades, decadeId]);

  const decadeEntries = useMemo(() => {
    if (!decadeId) return [];
    return entriesInDecade(entries, decadeId);
  }, [entries, decadeId]);

  useEffect(() => {
    if (!decadeEntries.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !decadeEntries.some((e) => e.id === selectedId)) {
      setSelectedId(decadeEntries[0].id);
    }
  }, [decadeEntries, selectedId]);

  const selected = decadeEntries.find((e) => e.id === selectedId) ?? null;

  if (!entries.length) {
    return <p className="text-foreground/60">{m.hof_timeline_empty()}</p>;
  }

  return (
    <div className="space-y-8">
      <div>
        <span className="eyebrow">{m.hof_timeline_eyebrow()}</span>
        <h2 className="mt-3 font-display text-3xl md:text-4xl">
          <EmphasizedTitle
            lead={m.hof_timeline_title_lead()}
            accent={m.hof_timeline_title_accent()}
          />
        </h2>
        <p className="mt-2 max-w-xl text-foreground/65">{m.hof_timeline_intro()}</p>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-white/10 pb-4">
        {HOF_DECADES.filter((d) => availableDecades.includes(d.id)).map((decade) => (
          <button
            key={decade.id}
            type="button"
            onClick={() => setDecadeId(decade.id)}
            className={cn(
              "font-display text-sm tracking-[0.18em] transition-colors",
              decadeId === decade.id
                ? "text-ember underline decoration-ember underline-offset-8"
                : "text-foreground/55 hover:text-foreground",
            )}
          >
            {decadeLabel(decade.id)}
          </button>
        ))}
      </div>

      <div className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2">
        {decadeEntries.map((entry) => (
          <HallOfFameTile
            key={entry.id}
            entry={entry}
            selected={entry.id === selectedId}
            onSelect={() => setSelectedId(entry.id)}
          />
        ))}
      </div>

      {selected ? <HallOfFameDetail key={selected.id} entry={selected} /> : null}
    </div>
  );
}
